import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, inject, viewChild } from '@angular/core';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { combineLatest, debounceTime, startWith, tap } from 'rxjs';
import { ConsumptionLimitRule, PunchCardReceiptToolType, PunchCardSchema, PunchCardStrategyType, TimeLimitRule } from 'src/app/api/api-campaigns.service.generated';
import { DateUtils } from 'src/app/common/utils/DateUtils';
import { SchemeRuleComponent } from '../../_/scheme-rule/scheme-rule.component';
import { ProductRulesEditComponent } from '../../_/product-rules-edit/product-rules-edit.component';
import { PunchCardCampaignService } from '../punch-card-campaign-edit.service';
import { requireDependentValidator } from 'src/app/validators/require-dependent.validator';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatCardModule } from '@angular/material/card';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';


@UntilDestroy()
@Component({
  selector: 'app-punch-card-scheme',
  templateUrl: './punch-card-scheme.component.html',
  styleUrls: ['./punch-card-scheme.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSlideToggleModule,
    MatCardModule,
    FlexLayoutModule,
    SchemeRuleComponent,
    ProductRulesEditComponent
  ]
})
export class PunchCardSchemeComponent implements OnInit, OnChanges {
  private punchCardCampaignService = inject(PunchCardCampaignService);
  private cdr = inject(ChangeDetectorRef);

  readonly productRules = viewChild<ProductRulesEditComponent>('productRules');
  readonly schemeRule = viewChild<SchemeRuleComponent>('schemeRule');

  @Input() schemeId: string;
  @Input() advertiserId: string;
  @Input() campaignId: string;
  @Input() allowFlow: boolean;
  @Input() strategyType: PunchCardStrategyType | undefined;
  @Input() captureToolType: string;
  @Input() scheme: PunchCardSchema;
  @Output() onSchemeChanged: EventEmitter<any> = new EventEmitter();

  isSecretCodeCaptureToolType: boolean;
  isHandshakeQR = false;
  private isSettingFormValue = false;
  private setFormValuePromise: Promise<void> | null = null;
  private viewInitialized = false;
  private pendingSchemeData: PunchCardSchema | null = null;

  form = new FormGroup({
    // stamps timeLimit
    timeLimitMaxLimit: new FormControl(null, requireDependentValidator('timeLimitInterval')),
    timeLimitInterval: new FormControl(null, requireDependentValidator('timeLimitMaxLimit')),

    // consumptionLimit
    consumptionLimitMaxLimit: new FormControl(null, requireDependentValidator('consumptionLimitInterval')),
    consumptionLimitInterval: new FormControl(null, requireDependentValidator('consumptionLimitMaxLimit')),

    // handshake settings
    periodDurationQrCode: new FormControl(null, []),
    distanceAllowed: new FormControl(null, [Validators.min(1)]),

    // handshake toggle controls
    enableQrDuration: new FormControl(true, []),
    enableDistanceLimit: new FormControl(true, []),

    // reward points
    senderPoints: new FormControl(null, [Validators.min(1)]),
    receiverPoints: new FormControl(null, [Validators.min(1)]),

    // points limit per interval (optional)
    senderPointsLimit: new FormControl(null, [Validators.min(1)]),
    senderPointsInterval: new FormControl(null, []),
    receiverPointsLimit: new FormControl(null, [Validators.min(1)]),
    receiverPointsInterval: new FormControl(null, []),

    // QR generation limits
    maxQrGenerationsLimit: new FormControl(null, [Validators.min(1)]),
    qrGenerationInterval: new FormControl(null, []),
    maxScannerLimit: new FormControl(null, [Validators.min(1)]),
    scannerInterval: new FormControl(null, []),

    schemeName: new FormControl(null, [Validators.required, Validators.maxLength(300)])
  });

  constructor() {
    this.addStampsTimeLimitValidator();
    this.addConsumptionLimitValidator();
  }

  async ngAfterViewInit() {
    this.viewInitialized = true;
    this.onProductRulesChange();
    this.onSchemeRuleChange();
    this.isHandshakeQR = this.captureToolType === PunchCardReceiptToolType.HandshakeQR;
    
    // If there's pending data from ngOnChanges, set it now
    if (this.pendingSchemeData) {
      await this.setFormValue();
      this.pendingSchemeData = null;
    }
    
    this.cdr.detectChanges();
  }

  onProductRulesChange() {
    this.productRules()?.onProductRulesChanged.subscribe(async () => {
      // Wait for setFormValue to complete before emitting
      if (this.setFormValuePromise) {
        await this.setFormValuePromise;
      }
      if (!this.isSettingFormValue) {
        this.onSchemeChanged.emit(this.getFormValue());
      }
    });
  }

  onSchemeRuleChange() {
    this.schemeRule()?.onSchemeRuleChanged.subscribe(async () => {
      // Wait for setFormValue to complete before emitting
      if (this.setFormValuePromise) {
        await this.setFormValuePromise;
      }
      if (!this.isSettingFormValue) {
        this.onSchemeChanged.emit(this.getFormValue());
      }
    });
  }

  onFormDataChange() {
    this.form.valueChanges
      .pipe(untilDestroyed(this))
      .pipe(debounceTime(500))
      .subscribe(async () => {
        if (this.setFormValuePromise) {
          await this.setFormValuePromise;
        }
        if (!this.isSettingFormValue) {
          this.onSchemeChanged.emit(this.getFormValue());
        }
      })
  }

  async ngOnChanges() {
    this.onFormDataChange();
    this.updateHandshakeQRValidators();
    
    // If view is not initialized yet, save data for later
    if (!this.viewInitialized) {
      this.pendingSchemeData = this.scheme;
      return;
    }
    
    await this.setFormValue();
    this.cdr.detectChanges();
  }

  ngOnInit(): void {
    this.isSecretCodeCaptureToolType = this.captureToolType == PunchCardReceiptToolType.SecretCode;
    this.punchCardCampaignService.captureToolTypeChanged.subscribe(newCaptureToolType => {
      this.isSecretCodeCaptureToolType = newCaptureToolType === PunchCardReceiptToolType.SecretCode;
      this.captureToolType = newCaptureToolType;
      this.updateHandshakeQRValidators();
    });
    this.addPeriodDurationValidator();
    this.updateHandshakeQRValidators();

    this.form.controls.enableQrDuration.valueChanges.subscribe((value) => {
      if (value) {
        this.form.get('periodDurationQrCode').enable();
      } else {
        this.form.get('periodDurationQrCode').disable();
      }
    });

    this.form.controls.enableDistanceLimit.valueChanges.subscribe((value) => {
      if (value) {
        this.form.get('distanceAllowed').enable();
      } else {
        this.form.get('distanceAllowed').disable();
      }
    });
  }

  addPeriodDurationValidator() {
    const ctrl = this.form.controls.periodDurationQrCode;

    combineLatest([
      ctrl.valueChanges.pipe(startWith(''))
    ])
      .pipe(
        tap(() => { })
      )
      .subscribe(([v]) => {
        if (v) {
          ctrl.setErrors(null);

          if (v && !DateUtils.isValidTimestamp(v))
            ctrl.setErrors({ invalidTs: true });

          return;
        }
      });
  }

  onInput(_ev) {
    setTimeout(() => {
      this.onSchemeChanged.emit(this.getFormValue());
    }, 300);
  }

  getFormValue() {

    if (!this.isValid()) {
      return undefined;
    }
    const schemeRules = this.schemeRule()?.getFormValue() || null;

    const scheme: PunchCardSchema = new PunchCardSchema({
      name: this.form.get('schemeName').value,
      id: this.scheme.id || this.schemeId,
      campaignId: this.campaignId,
      consumptionLimit: this.getConsumptionLimit(),
      timeLimit: this.getTimeLimit(),
      productRules: this.productRules()?.getFormValue(),
      periodDurationQrCode: this.form.get('periodDurationQrCode').value,
      distanceAllowed: this.form.get('distanceAllowed').value,
      senderPoints: this.form.get('senderPoints').value,
      receiverPoints: this.form.get('receiverPoints').value,
      senderPointsLimit: this.form.get('senderPointsLimit').value,
      senderPointsInterval: this.form.get('senderPointsInterval').value,
      receiverPointsLimit: this.form.get('receiverPointsLimit').value,
      receiverPointsInterval: this.form.get('receiverPointsInterval').value,
      maxQrGenerationsLimit: this.form.get('maxQrGenerationsLimit').value,
      qrGenerationInterval: this.form.get('qrGenerationInterval').value,
      maxScannerLimit: this.form.get('maxScannerLimit').value,
      scannerInterval: this.form.get('scannerInterval').value,
      enableQrDuration: this.form.get('enableQrDuration').value,
      enableDistanceLimit: this.form.get('enableDistanceLimit').value,
      ...schemeRules
    })

    return scheme;
  }

  private getConsumptionLimit() {
    const consumptionLimit: ConsumptionLimitRule = new ConsumptionLimitRule();

    if (this.form.get('consumptionLimitInterval').value) {
      consumptionLimit.interval = this.form.get('consumptionLimitInterval').value;
    }
    if (this.form.get('consumptionLimitMaxLimit').value) {
      consumptionLimit.maxLimit = this.form.get('consumptionLimitMaxLimit').value;
    }

    return consumptionLimit;
  }

  private getTimeLimit() {
    const timeLimit: TimeLimitRule = new TimeLimitRule();
    if (this.form.get('timeLimitInterval').value) {
      timeLimit.interval = this.form.get('timeLimitInterval').value
    }

    if (this.form.get('timeLimitMaxLimit').value) {
      timeLimit.maxLimit = this.form.get('timeLimitMaxLimit').value
    }

    return timeLimit;
  }

  async setFormValue() {
    // Wait for view to stabilize
    this.setFormValuePromise = this._setFormValueInternal();
    await this.setFormValuePromise;
    this.setFormValuePromise = null;
  }

  private async _setFormValueInternal() {
    this.isSettingFormValue = true;

    try {
      if (!this.scheme) return;

      this.form.patchValue({
        schemeName: this.scheme.name || '',
        timeLimitInterval: this.scheme.timeLimit?.interval || null,
        timeLimitMaxLimit: this.scheme.timeLimit?.maxLimit || null,
        consumptionLimitInterval: this.scheme.consumptionLimit?.interval || null,
        consumptionLimitMaxLimit: this.scheme.consumptionLimit?.maxLimit || null,
        periodDurationQrCode: this.scheme.periodDurationQrCode || null,
        distanceAllowed: this.scheme.distanceAllowed || null,
        senderPoints: this.scheme.senderPoints || null,
        receiverPoints: this.scheme.receiverPoints || null,
        senderPointsLimit: this.scheme.senderPointsLimit || null,
        senderPointsInterval: this.scheme.senderPointsInterval || null,
        receiverPointsLimit: this.scheme.receiverPointsLimit || null,
        receiverPointsInterval: this.scheme.receiverPointsInterval || null,
        maxQrGenerationsLimit: this.scheme.maxQrGenerationsLimit || null,
        qrGenerationInterval: this.scheme.qrGenerationInterval || null,
        maxScannerLimit: this.scheme.maxScannerLimit || null,
        scannerInterval: this.scheme.scannerInterval || null,
        enableQrDuration: this.scheme.enableQrDuration !== undefined ? this.scheme.enableQrDuration : true,
        enableDistanceLimit: this.scheme.enableDistanceLimit !== undefined ? this.scheme.enableDistanceLimit : true,
      }, { emitEvent: false });

      // Wait for Angular render
      await new Promise(resolve => setTimeout(resolve, 0));

      // Set product rules with retry logic
      if (this.scheme?.productRules?.length > 0) {
        const maxRetries = 5;
        let retryCount = 0;
        let productRulesComponent = this.productRules();
        
        // Retry if component is not ready yet
        while (!productRulesComponent && retryCount < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 50));
          productRulesComponent = this.productRules();
          retryCount++;
        }
        
        if (productRulesComponent) {
          await productRulesComponent.setFormValue(this.scheme.productRules);
        }
      }
      
      // Wait additional time to ensure child components are fully completed
      await new Promise(resolve => setTimeout(resolve, 50));

    } finally {
      // Reset flag
      this.isSettingFormValue = false;
    }
  }

  addStampsTimeLimitValidator() {
    const ctrl = this.form.controls.timeLimitInterval;

    combineLatest([
      ctrl.valueChanges.pipe(startWith(''))
    ])
      .pipe(
        tap(() => { })
      )
      .subscribe(([v]) => {
        // valid if both fields have/have not value
        if ((v)) {
          // ctrl1.setErrors(null);
          ctrl.setErrors(null);

          if (v && !DateUtils.isValidTimestamp(v))
            ctrl.setErrors({ invalidTs: true });

          return;
        }

        // if (!v1 && v2) ctrl1.setErrors({ invalidLimit: true });

      });
  }

  forceValidatorsToDisplayErrors() {
    if (!this.isHandshakeQR) {
      this.schemeRule().forceValidatorsToDisplayErrors();
      this.productRules().forceValidatorsToDisplayErrors();
    }
    this.form.markAllAsTouched();
  }

  displayDuration(duration: string) {
    return DateUtils.displayCsDuration(duration);
  }

  addConsumptionLimitValidator() {
    const ctrl = this.form.controls.consumptionLimitInterval;

    combineLatest([
      ctrl.valueChanges.pipe(startWith(''))
    ])
      .pipe(
        tap(() => { })
      )
      .subscribe(([v]) => {
        // valid if both fields have/have not value
        if (v) {
          ctrl.setErrors(null);

          if (v && !DateUtils.isValidTimestamp(v))
            ctrl.setErrors({ invalidConsumptionTs: true });

          return;
        }

      });
  }

  updateHandshakeQRValidators() {
    const isHandshakeQR = this.captureToolType === PunchCardReceiptToolType.HandshakeQR;

    const handshakeQRFields = [
      'periodDurationQrCode',
      'distanceAllowed',
      'senderPoints',
      'receiverPoints'
    ];

    handshakeQRFields.forEach(fieldName => {
      const control = this.form.get(fieldName);
      if (control) {
        if (isHandshakeQR) {
          // For number fields, combine required and min validators
          if (fieldName === 'distanceAllowed' || fieldName === 'senderPoints' || fieldName === 'receiverPoints') {
            control.setValidators([Validators.required, Validators.min(1)]);
          } else {
            control.setValidators([Validators.required]);
          }
          control.markAsTouched();
        } else {
          // When not HandshakeQR, restore original validators
          if (fieldName === 'distanceAllowed' || fieldName === 'senderPoints' || fieldName === 'receiverPoints') {
            control.setValidators([Validators.min(1)]);
          } else {
            control.clearValidators();
          }
          control.markAsUntouched();
        }
        control.updateValueAndValidity();
      }
    });

    if (isHandshakeQR) {
      this.addPeriodDurationValidator();
    }
  }

  isValid(): boolean {
    if (this.captureToolType === PunchCardReceiptToolType.HandshakeQR) {
      if (!this.form.valid) {
        this.forceValidatorsToDisplayErrors();
        return false;
      }
      return true;
    }

    const productRulesValid = this.productRules()?.isValid() ?? true;
    const schemeRuleValid = this.schemeRule()?.isValid() ?? true;

    if (!productRulesValid || !schemeRuleValid) {
      return false;
    }

    if (!this.form.valid) {
      this.forceValidatorsToDisplayErrors();
      return false;
    }

    return true;
  }
}
